/**
 * 詢問表單那道閘的前端這一半（AV P6）。
 *
 * 權威的閘在後端 `inquiry.CreateInquiry`（撞期時零寫入、場主收不到）。這裡做的只是
 * 把同一個判斷提早顯示，讓租客不用送出才知道撞到——**不是第二份規則**，所以這個檔案
 * 一行時段運算都不做，只讀 `/api/space/slot-check` 回來的 `conflict` 與 `available`。
 *
 * ⚠️ **fail-open 是硬要求。** 認不得的值、缺欄位、請求失敗一律當 `unknown`（＝不擋）。
 * 方向不對稱：前端漏擋，頂多讓租客白跑一次往返，後端照樣擋得住；前端誤擋，則是
 * 全站送不出詢問——一場災難。所以不確定的時候永遠倒向放行。
 *
 * （日曆那四態的 `dayState` / `stateSymbol` 在 `utils/spaceSchedule.ts`，答的是
 * 「這一天長什麼樣」；這裡答的是「這一次送不送得出去」。）
 */

/** 對應 `domain.SlotConflict*`。 */
export type SlotConflict = 'none' | 'partial' | 'full' | 'closed' | 'unknown'

export interface SlotVerdict {
    conflict: SlotConflict
    /** 那一天還訂得到的區間，如 `['09:00~12:00']`。 */
    available: string[]
}

/** 只有 `partial` 與 `full` 說得出「已有人租用」這句肯定句，也只有這兩態會擋。 */
function isBlocking(conflict: SlotConflict): boolean {
    return conflict === 'partial' || conflict === 'full'
}

function isSlotConflict(value?: string): value is SlotConflict {
    return value === 'none' || value === 'partial' || value === 'full'
        || value === 'closed' || value === 'unknown'
}

/** 還沒問、或問不到答案時的值。 */
export const UNKNOWN_VERDICT: SlotVerdict = {conflict: 'unknown', available: []}

export interface SlotCheckResponse {
    conflict?: string
    available?: string[]
}

/**
 * 把端點回應收斂成 `SlotVerdict`。
 *
 * `undefined`（`composables/requset.ts` 把後端錯誤吞成 `undefined`）、欄位缺漏、
 * 沒見過的 `conflict` 值一律回 `unknown`——見檔頭的 fail-open。
 */
export function toSlotVerdict(raw?: SlotCheckResponse | null): SlotVerdict {
    if (!raw || !isSlotConflict(raw.conflict)) {
        return UNKNOWN_VERDICT
    }
    return {
        conflict: raw.conflict,
        available: Array.isArray(raw.available)
            ? raw.available.filter((slot) => typeof slot === 'string' && slot !== '')
            : []
    }
}

/**
 * 這一次送出該不該擋。
 *
 * `broadcast` 為真時一律不擋：廣播的用意本來就是「這家不行就找相近的」，撞檔期
 * 正是最該廣播的時刻。後端 `createBroadcastInquiry` 的做法一致——把撞期的主場地
 * 從收件人清單剔掉，其餘照常寄出，剔完為空才回 `IQ0014`。
 */
export function submitBlocked(verdict: SlotVerdict, broadcast: boolean): boolean {
    if (broadcast) {
        return false
    }
    return isBlocking(toSlotVerdict(verdict).conflict)
}

/** ⛔ 方框裡「改成 HH:MM~HH:MM」那顆快捷鍵要填的值；沒有可選區間時回空字串。 */
export function firstAvailable(verdict: SlotVerdict): string {
    return toSlotVerdict(verdict).available[0] ?? ''
}
